import type { RefObject } from 'react';

type ListResponse<T> = { data?: T[] };

export function buildSearchKey(...parts: Array<string | number>): string {
  return parts
    .map((part) =>
      String(part ?? '')
        .trim()
        .toLowerCase(),
    )
    .join(':');
}

export function getNextRequestId(seqRef: RefObject<number>): number {
  return ++seqRef.current;
}

export function getNextRowRequestId(
  seqByRowRef: RefObject<Record<number, number>>,
  rowId: number,
): number {
  const next = (seqByRowRef.current[rowId] ?? 0) + 1;
  seqByRowRef.current[rowId] = next;
  return next;
}

export function isLatestRequest(
  seqRef: RefObject<number>,
  requestId: number,
): boolean {
  return seqRef.current === requestId;
}

export function isLatestRowRequest(
  seqByRowRef: RefObject<Record<number, number>>,
  rowId: number,
  requestId: number,
): boolean {
  return seqByRowRef.current[rowId] === requestId;
}

export async function getCachedOrFetchList<T>(params: {
  key: string;
  cacheRef: RefObject<Map<string, T[]>>;
  inFlightRef: RefObject<Map<string, Promise<ListResponse<T>>>>;
  fetcher: () => Promise<ListResponse<T>>;
}): Promise<T[]> {
  const { key, cacheRef, inFlightRef, fetcher } = params;

  const cached = cacheRef.current.get(key);
  if (cached) return cached;

  const inFlight = inFlightRef.current.get(key);
  if (inFlight) {
    const response = await inFlight;
    return response.data || [];
  }

  const request = fetcher().finally(() => {
    inFlightRef.current.delete(key);
  });
  inFlightRef.current.set(key, request);

  const response = await request;
  const list = response.data || [];
  cacheRef.current.set(key, list);
  return list;
}
